import React from "react";
import Typograf from "typograf";
import styles from './typograf.module.scss';
import {translatePartnerText} from '../locales/language';

const typograf = new Typograf({locale: ['ru', 'en-US']});
const typografWithLastWordWrap = new Typograf({locale: ['ru', 'en-US']});
const typografWithShortWordList = new Typograf({locale: ['ru', 'en-US']});

// Keep linguistic short-word rules, without binding every two-letter word.
typografWithShortWordList.disableRule([
    'common/nbsp/afterShortWord',
    'common/space/trimLeft',
    'common/space/trimRight',
    'common/space/delTrailingBlanks',
]);

typograf.disableRule([
    'common/space/trimLeft',
    'common/space/trimRight',
    'common/space/delTrailingBlanks',
]);

typografWithLastWordWrap.disableRule([
    'common/space/trimLeft',
    'common/space/trimRight',
    'common/space/delTrailingBlanks',
    'common/nbsp/beforeShortLastWord',
]);

export function typografText(text: string): string {
    return typeof text === 'string' ? typograf.execute(translatePartnerText(text)) : text;
}

function wrapWholeWords(text: string): React.ReactNode {
    if (!text || /^[ \t\r\n]+$/.test(text)) {
        return text;
    }

    const parts = text.split(/([ \t\r\n]+)/);

    return (
        <span className={styles.text}>
            {parts.map((part, index) => {
                if (!part || /^[ \t\r\n]+$/.test(part)) {
                    return part;
                }

                return <span className={styles.word} data-typography-word key={index}>{part}</span>;
            })}
        </span>
    );
}

export function typografHeadingNode(text: string): React.ReactNode {
    return wrapWholeWords(typografWithShortWordList.execute(translatePartnerText(text)));
}

export function typografNode(node: React.ReactNode, allowShortLastWordWrap = false): React.ReactNode {
    if (typeof node === 'string') {
        if (allowShortLastWordWrap) {
            return wrapWholeWords(typografWithLastWordWrap.execute(translatePartnerText(node)));
        }

        return wrapWholeWords(typografText(node));
    }

    if (Array.isArray(node)) {
        return React.Children.map(node, child => typografNode(child, allowShortLastWordWrap));
    }

    if (React.isValidElement(node)) {
        const element = node as React.ReactElement<{children?: React.ReactNode; className?: string}>;

        if (element.props.className === styles.text || element.props.className === styles.word) {
            return element;
        }

        return React.cloneElement(element, undefined, typografNode(element.props.children, allowShortLastWordWrap));
    }

    return node;
}
